import { Object as TsObject } from 'ts-toolbelt';
import metadataBuilder from './metadataBuilder';
import { drillDetailBuilder } from './drillDetailBuilder';
import reportStyleBuilder from './styleBuilders/reportStyleBuilder';

import isRowNumberType from '../utils/isRowNumberType';

import { APP_NAME, SheetExportTypes } from '../xlsxExport.constants';
import {
  DATA,
  COLUMN_HEADER,
  PARENT_DATA,
  DATA_BORDER_TOPLINE,
  EMPTY,
  PARENT_SUMMARY,
  CHILD_DATA,
  RowTypes,
} from '../constants/rowTypes';
import { STRING } from '../constants/dataTypes';
import { CUSTOM_CLASSES } from '../constants/customClasses';
import { flattenChildrenTree } from './helpers/reportBuilderHelpers';
import { ColumnDefinition, ExcelCellValue } from '@common/xlsxExport/types';
import { CombinedRow, ReportRow, DetailRow } from '@common/xlsxExport/builders/reportBuilderTypes';


const shouldInsertEmptyRow = (rowType: RowTypes, previousRowType: RowTypes) => {
  const isParentDataAndPreviousRowWasNotColumnHeaders
    = rowType === PARENT_DATA && previousRowType !== COLUMN_HEADER;
  const isDataAndPreviousRowWasNotData
    = rowType === DATA
    && (previousRowType !== DATA
      && previousRowType !== DATA_BORDER_TOPLINE
      && previousRowType !== COLUMN_HEADER);
  const isDataBorderTopline = rowType === DATA_BORDER_TOPLINE;

  return (
    isParentDataAndPreviousRowWasNotColumnHeaders
    || isDataAndPreviousRowWasNotData
    || isDataBorderTopline
  );
};

type UnpackedChildren = TsObject.Optional<Omit<CombinedRow, 'children'>, 'columns' | 'description'>;
const unpackChildren = (rows: CombinedRow[]) =>
  rows.reduce<UnpackedChildren[]>((acc, parentRow, index) => {
    const childRows = flattenChildrenTree(parentRow);

    const currentRow = parentRow.summaryDescription
      ? {
        rowType: parentRow.rowType,
        description: parentRow.description,
      }
      : {
        rowType: parentRow.rowType,
        description: parentRow.description,
        columns: parentRow.columns,
        trailingText: parentRow.trailingText,
        preText: parentRow.preText,
        customClasses: parentRow.customClasses,
      };

    const previousRow = rows[index - 1];
    const rowType = parentRow && parentRow.rowType;
    const previousRowType = previousRow && previousRow.rowType;

    const hasNoMarginCustomClass = parentRow && parentRow.customClasses
      && parentRow.customClasses.includes(CUSTOM_CLASSES.noMarginClass);
    const noEmptyRowOverride = (parentRow && parentRow.noEmptyRowOverride) || hasNoMarginCustomClass;

    const emptyRow = (!noEmptyRowOverride && shouldInsertEmptyRow(rowType, previousRowType))
      ? [{ rowType: EMPTY as RowTypes }]
      : [];

    const summaryRows = parentRow.summaryDescription
      ? [{
        rowType: PARENT_SUMMARY as RowTypes,
        description: parentRow.summaryDescription,
        columns: parentRow.columns,
        trailingText: parentRow.summaryTrailingText || parentRow.trailingText,
      }]
      : [];

    return [...acc, ...emptyRow, currentRow, ...childRows, ...summaryRows];
  }, []);


const formatTo2DArray = (data: UnpackedChildren[], withPreText = false, cellsNumber: number, {
  splitDescriptionColumn,
  groupByTagEnabled = false,
  dataColumnStartIndex,
  dataColumnEndIndex,
}: {
    splitDescriptionColumn?: boolean,
    groupByTagEnabled?: boolean,
    dataColumnStartIndex?: number,
    dataColumnEndIndex?: number,
}) =>
  data.map(row => {
    const { columns, trailingText, rowType } = row;
    if (rowType === EMPTY) {
      return [''];
    }

    const description = row.description;
    const account = row.account;
    const accountDescription = row.accountDescription;
    const tagName = row.tagName;
    const getDescription = () => {
      if (!splitDescriptionColumn) {
        return [description];
      }

      const addTagNameColumn = (columns: ExcelCellValue[]) => {
        if (!groupByTagEnabled) {
          return [...columns, tagName];
        }

        if (rowType === CHILD_DATA) {
          return [null, ...columns];
        }

        return [...columns, null];
      };

      return account
        ? addTagNameColumn([account, accountDescription])
        : addTagNameColumn([description, null]);
    };
    const accountAndDescription = getDescription();
    const preText = withPreText ? [row.preText] : [];

    if (!columns) {
      return [...preText, description];
    }

    const cells = Array(cellsNumber).fill(null);

    const columnsKeys = Object.keys(columns).map(Number);

    (typeof dataColumnStartIndex === 'number' && typeof dataColumnEndIndex === 'number'
      ? columnsKeys.slice(dataColumnStartIndex, dataColumnEndIndex)
      : columnsKeys
    ).forEach(columnKey => {
      if (columns[columnKey].value && isRowNumberType(row) && columns[columnKey].type !== STRING) {
        const parsedNumber = Number(columns[columnKey].value);
        cells[columnKey] = isNaN(parsedNumber) ? 0 : parsedNumber;
        return;
      }

      cells[columnKey] = columns[columnKey].value;
    });

    const trailingTextRows = trailingText ? [trailingText] : [];

    return [...preText, ...accountAndDescription, ...cells, ...trailingTextRows];
  });


export type ReportBuilderSheet = {
    type: SheetExportTypes.REPORT,

    name: string,
    data: {
        columnDefs: ColumnDefinition[],
        columnCount: number,
        rows: ReportRow[],
    },
    descriptionColumnIndex?: number,
    options: {
        hideDecimals?: boolean,
        shouldDisplayZero?: boolean,
        negativeMinus?: boolean,
        addTimestamp?: boolean,
        splitDescriptionColumn?: boolean,
        groupByTag?: boolean,
        numberOfColumnsToStick: number,
        dataColumnStartIndex: number,
        dataColumnEndIndex: number,
        isFunctionalCurrency: boolean,
        detailRows?: {
            isValid?: boolean,
            data: {
                [reportLineType: string]: {
                    data: DetailRow[],
                }
            }
        }
    }
}

export type ReportBuilderOptions = {
    description?: string,
    periodName?: string,
}


const reportBuilder = (sheet: ReportBuilderSheet, options: ReportBuilderOptions = {}) => {
  const {
    description,
    periodName,
  } = options;
  const {
    hideDecimals,
    shouldDisplayZero,
    negativeMinus,
    addTimestamp,
    splitDescriptionColumn,
    groupByTag = false,
    dataColumnStartIndex,
    dataColumnEndIndex,
  } = sheet.options;

  const sheetMetadata = metadataBuilder({
    appName: APP_NAME,
    sheetTitle: sheet.name,
    sheetDescription: description,
    periodName,
    addTimestamp,
  });

  const combinedRows = drillDetailBuilder(sheet, { groupByTag });
  const unpackedRows = unpackChildren(combinedRows);

  const withPreText = unpackedRows.some(row => row.preText);
  const dataGrid = formatTo2DArray(unpackedRows, withPreText, sheet.data.columnCount, {
    splitDescriptionColumn,
    groupByTagEnabled: groupByTag,
    dataColumnStartIndex,
    dataColumnEndIndex,
  });

  const grid = [...sheetMetadata, ...dataGrid];

  const metadata = reportStyleBuilder({
    sheetName: sheet.name,
    numberOfRowsToStick: sheetMetadata.length + 2, // header row and details row
    numberOfColumnsToStick: sheet.options.numberOfColumnsToStick,
    columnCount: sheet.data.columnCount,
    columnDefinitions: sheet.data.columnDefs,
    rows: unpackedRows,
    sheetMetadataLength: sheetMetadata.length,
    descriptionColumnIndex: sheet.descriptionColumnIndex,
    withPreText,
    hideDecimals,
    shouldDisplayZero,
    negativeMinus,
    splitDescriptionColumn,
  });

  return {
    metadata,
    grid,
  };
};

export default reportBuilder;
